import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Search, ArrowUpDown, ArrowUp, ArrowDown, User, Phone,
  Star, MessageSquare, CheckCircle, Calendar,
  Wifi, QrCode, Smartphone, SlidersHorizontal, AlertCircle,
  X, Quote, Utensils, Sparkles, Wind, BadgeDollarSign, RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import customerService from "@/services/customerService";
import Pagination from "@/components/shared/Pagination";

/* ─── Keyframes injected once ─── */
const injectStyles = () => {
  if (document.getElementById("fb-v2-styles")) return;
  const s = document.createElement("style");
  s.id = "fb-v2-styles";
  s.textContent = `
    @keyframes fb-up    { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
    @keyframes fb-in    { from { opacity:0; transform:scale(0.96); }      to { opacity:1; transform:scale(1); }      }
    @keyframes fb-slide { from { opacity:0; transform:translateX(-6px); } to { opacity:1; transform:translateX(0); } }
    @keyframes fb-shine { 0%,100%{background-position:-200% 0} 50%{background-position:200% 0} }
    .fb-card-item { animation: fb-up  0.3s ease both; }
    .fb-stat      { animation: fb-in  0.22s ease both; }
    .fb-panel     { animation: fb-slide 0.2s ease both; }
    .fb-shimmer   {
      background: linear-gradient(90deg,#f5f5f5 25%,#ebebeb 50%,#f5f5f5 75%);
      background-size: 200% 100%;
      animation: fb-shine 1.4s infinite;
    }
    .fb-card-item { transition: transform 0.18s ease, box-shadow 0.18s ease; }
    .fb-card-item:hover { transform: translateY(-1px); box-shadow: 0 8px 32px -4px rgba(244,63,94,.10), 0 2px 8px -2px rgba(0,0,0,.06); }
    .s-on  { color:#f59e0b; fill:#f59e0b; }
    .s-off { color:#e5e7eb; fill:#f3f4f6; }
  `;
  document.head.appendChild(s);
};

/* ════ helpers ════ */
const toInputDate = (d) => format(d, "yyyy-MM-dd");

function Stars({ value = 0, size = 13 }) {
  return (
    <div className="flex gap-[2px]">
      {[1,2,3,4,5].map(n => (
        <Star key={n} size={size} className={n <= value ? "s-on" : "s-off"} />
      ))}
    </div>
  );
}

function sentimentColor(v) {
  if (!v) return { bar:"bg-gray-200", text:"text-gray-400", border:"border-gray-200" };
  if (v >= 4) return { bar:"bg-emerald-400", text:"text-emerald-600", border:"border-emerald-200" };
  if (v === 3) return { bar:"bg-amber-400",  text:"text-amber-600",   border:"border-amber-200"   };
  return             { bar:"bg-rose-400",    text:"text-rose-600",    border:"border-rose-200"     };
}

function MiniStarRow({ label, value, Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2">
      <Icon size={10} className="text-gray-400 flex-shrink-0" />
      <span className="text-[10px] text-gray-400 w-14 flex-shrink-0">{label}</span>
      <Stars value={value} size={10} />
      <span className="text-[10px] font-bold text-gray-500 ml-0.5">{value}</span>
    </div>
  );
}

const SRC = {
  MANUAL:   { label:"In-store", Icon:User,        cls:"bg-slate-100 text-slate-500 border-slate-200"  },
  QR_CODE:  { label:"QR Code",  Icon:QrCode,      cls:"bg-violet-50 text-violet-600 border-violet-200" },
  ONLINE:   { label:"Online",   Icon:Wifi,         cls:"bg-sky-50 text-sky-600 border-sky-200"          },
  PHONE:    { label:"Phone",    Icon:Smartphone,   cls:"bg-teal-50 text-teal-600 border-teal-200"       },
  TAKEAWAY: { label:"Takeaway", Icon:Utensils,     cls:"bg-orange-50 text-orange-600 border-orange-200" },
};
function SourceBadge({ source }) {
  const { label, Icon, cls } = SRC[source] || SRC.MANUAL;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${cls}`}>
      <Icon size={9} />{label}
    </span>
  );
}

function StatusChip({ resolved }) {
  return resolved ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200">
      <CheckCircle size={9} />Resolved
    </span>
  ) : null;
}

function StatCard({ label, value, icon: Icon, iconBg, iconColor, valueCls = "text-gray-900", delay = 0 }) {
  return (
    <div
      className="fb-stat flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3.5 shadow-sm"
      style={{ animationDelay:`${delay}ms` }}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon size={15} className={iconColor} />
      </div>
      <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{label}</span>
      <div className="flex-1" />
      <span className={`text-base font-extrabold leading-none ${valueCls}`}>{value}</span>
    </div>
  );
}

function ShimmerCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <div className="flex gap-4">
        <div className="fb-shimmer w-10 h-10 rounded-xl flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="fb-shimmer h-4 w-40 rounded-lg" />
          <div className="fb-shimmer h-3 w-24 rounded-lg" />
        </div>
        <div className="fb-shimmer h-6 w-16 rounded-full" />
      </div>
      <div className="mt-4 space-y-1.5">
        <div className="fb-shimmer h-3 w-full rounded-lg" />
        <div className="fb-shimmer h-3 w-5/6 rounded-lg" />
      </div>
    </div>
  );
}

/* ════ FEEDBACK CARD ════ */
function FeedbackCard({ item, idx }) {
  const [showItems, setShowItems] = useState(false);
  const { border, text: ratingText } = sentimentColor(item.overallRating);
  const initial = item.customerName?.[0]?.toUpperCase() ?? "?";
  const hasSubRatings = item.foodRating || item.serviceRating || item.ambienceRating || item.valueRating;
  const hasItemRatings = item.itemRatings?.length > 0;

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  };

  return (
    <div
      className={`fb-card-item relative bg-white rounded-2xl border ${border} shadow-sm overflow-hidden`}
      style={{ animationDelay:`${idx * 45}ms` }}
    >
      {/* Left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${
        item.overallRating >= 4 ? "bg-emerald-400"
        : item.overallRating === 3 ? "bg-amber-400"
        : item.overallRating ? "bg-rose-400"
        : "bg-gray-200"
      }`} />

      <div className="pl-5 pr-5 pt-4 pb-4">
        {/* ── Header ── */}
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-extrabold text-white"
            style={{ background:"linear-gradient(135deg,#f43f5e,#fb923c)", boxShadow:"0 2px 10px rgba(244,63,94,.25)" }}
          >
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-gray-900">{item.customerName || "Anonymous"}</p>
              {item.billNumber && (
                <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md font-medium">
                  Bill #{item.billNumber}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              {item.customerPhone && (
                <span className="text-[11px] text-gray-400 flex items-center gap-1">
                  <Phone size={9} />{item.customerPhone}
                </span>
              )}
              <span className="text-[11px] text-gray-400 flex items-center gap-1">
                <Calendar size={9} />{formatDate(item.createdAt)}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <div className="flex items-center gap-1">
              <span className={`text-lg font-extrabold leading-none ${ratingText}`}>
                {item.overallRating ? item.overallRating.toFixed(1) : "—"}
              </span>
              <span className="text-xs text-gray-300 font-medium">/5</span>
            </div>
            <Stars value={item.overallRating} size={12} />
          </div>
        </div>

        {/* ── Review text ── */}
        {item.reviewText ? (
          <div className="mt-3 relative">
            <Quote size={14} className="absolute -top-1 -left-0.5 text-rose-200 rotate-180" />
            <p className="text-sm text-gray-600 leading-relaxed pl-5 line-clamp-2 italic">{item.reviewText}</p>
          </div>
        ) : (
          <p className="mt-2.5 text-xs text-gray-300 italic pl-0.5">No written review</p>
        )}

        <div className="mt-3.5 border-t border-gray-50" />

        {/* ── Footer: sub-ratings + badges ── */}
        <div className="mt-3 flex items-end gap-4">
          {hasSubRatings && (
            <div className="flex-1 space-y-1.5 min-w-0">
              <MiniStarRow label="Food"     value={item.foodRating}     Icon={Utensils} />
              <MiniStarRow label="Service"  value={item.serviceRating}  Icon={Sparkles} />
              <MiniStarRow label="Ambience" value={item.ambienceRating} Icon={Wind} />
              <MiniStarRow label="Value"    value={item.valueRating}    Icon={BadgeDollarSign} />
            </div>
          )}
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0 ml-auto">
            <SourceBadge source={item.feedbackSource} />
            <StatusChip resolved={item.isResolved} />
          </div>
        </div>

        {/* ── Item Ratings ── */}
        {hasItemRatings && (
          <>
            <div className="mt-3 border-t border-gray-50" />
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowItems((v) => !v)}
                className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 hover:text-rose-500 transition-colors"
              >
                <Utensils size={10} />
                {showItems ? "Hide" : "Show"} item ratings
                <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full text-[9px] font-bold">
                  {item.itemRatings.length}
                </span>
              </button>
              {showItems && (
                <div className="mt-2 space-y-2">
                  {item.itemRatings.map((ir) => (
                    <div
                      key={ir.menuItemId}
                      className="flex items-start gap-2 bg-gray-50 rounded-xl px-3 py-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-gray-700 truncate">{ir.menuItemName}</p>
                        {ir.comment && (
                          <p className="text-[10px] text-gray-400 italic mt-0.5 line-clamp-1">{ir.comment}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Stars value={ir.rating} size={9} />
                        <span className="text-[10px] font-bold text-amber-600">{ir.rating}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ════ DATE RANGE BAR ════ */
const DATE_PRESETS = [
  { label:"Today",        value:"today"      },
  { label:"Yesterday",    value:"yesterday"  },
  { label:"Last 7 Days",  value:"last7days"  },
  { label:"Last 30 Days", value:"last30days" },
];

function resolvePreset(preset) {
  const today = new Date();
  switch (preset) {
    case "today":
      return { fromDate: toInputDate(today), toDate: toInputDate(today) };
    case "yesterday": {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { fromDate: toInputDate(y), toDate: toInputDate(y) };
    }
    case "last30days": {
      const s = new Date(today); s.setDate(s.getDate() - 29);
      return { fromDate: toInputDate(s), toDate: toInputDate(today) };
    }
    case "last7days":
    default: {
      const s = new Date(today); s.setDate(s.getDate() - 6);
      return { fromDate: toInputDate(s), toDate: toInputDate(today) };
    }
  }
}

function DateRangeBar({ fromDate, toDate, onApply, onRefresh }) {
  const [activePreset, setActivePreset] = useState("last7days");
  const [localFrom,    setLocalFrom]    = useState(fromDate);
  const [localTo,      setLocalTo]      = useState(toDate);
  const [fromError,    setFromError]    = useState(false);
  const [toError,      setToError]      = useState(false);

  const rangeLabel = useMemo(() => {
    if (activePreset === "today")      return "Today";
    if (activePreset === "yesterday")  return "Yesterday";
    if (activePreset === "last7days")  return "Last 7 Days";
    if (activePreset === "last30days") return "Last 30 Days";
    if (localFrom && localTo)
      return `${format(new Date(localFrom), "MMM dd")} – ${format(new Date(localTo), "MMM dd")}`;
    if (localFrom) return format(new Date(localFrom), "MMM dd");
    return "All time";
  }, [activePreset, localFrom, localTo]);

  const handlePreset = (preset) => {
    setActivePreset(preset);
    setFromError(false); setToError(false);
    const r = resolvePreset(preset);
    setLocalFrom(r.fromDate); setLocalTo(r.toDate);
    onApply(r.fromDate, r.toDate);
  };

  const isApplyDisabled = () => {
    if (!localFrom) return true;
    if (localFrom && localTo && new Date(localFrom) > new Date(localTo)) return true;
    return false;
  };

  const handleApply = () => {
    setFromError(false); setToError(false);
    if (localFrom && localTo && new Date(localFrom) > new Date(localTo)) {
      setFromError(true); setToError(true); return;
    }
    setActivePreset(null);
    onApply(localFrom, localTo || localFrom);
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-rose-50 rounded-lg">
            <Calendar className="w-5 h-5 text-rose-500" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Viewing feedback for</p>
            <p className="text-base font-semibold text-gray-900">{rangeLabel}</p>
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          {DATE_PRESETS.map((p) => (
            <button key={p.value} onClick={() => handlePreset(p.value)}
              className={`px-4 py-2 h-10 flex items-center rounded-lg text-sm font-medium transition ${
                activePreset === p.value
                  ? "bg-rose-500 text-white"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}>
              {p.label}
            </button>
          ))}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700 mb-1">From</label>
            <input type="date" value={localFrom}
              onChange={e => { setLocalFrom(e.target.value); setActivePreset(null); setFromError(false); setToError(false); }}
              className={`px-3 py-2 h-10 border rounded-lg text-sm ${fromError ? "border-red-500" : "border-gray-300"}`} />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700 mb-1">To</label>
            <input type="date" value={localTo}
              onChange={e => { setLocalTo(e.target.value); setActivePreset(null); setFromError(false); setToError(false); }}
              className={`px-3 py-2 h-10 border rounded-lg text-sm ${toError ? "border-red-500" : "border-gray-300"}`} />
          </div>
          <button onClick={handleApply} disabled={isApplyDisabled()}
            className={`px-4 py-2 h-10 rounded-lg text-sm font-medium ${
              isApplyDisabled() ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-rose-500 text-white hover:bg-rose-600"
            }`}>
            Apply
          </button>
          <button onClick={onRefresh}
            className="p-2 h-10 w-10 flex items-center justify-center rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════ MAIN ════ */
export default function FeedbackList({ renderActions }) {
  useEffect(() => { injectStyles(); }, []);

  const initialRange = resolvePreset("last7days");

  const [feedbacks,      setFeedbacks]      = useState([]);
  const [totalElements,  setTotalElements]  = useState(0);
  const [totalPages,     setTotalPages]     = useState(1);
  const [summary,        setSummary]        = useState(null);
  const [loading,        setLoading]        = useState(false);

  const [filterText,    setFilterText]   = useState("");
  const [ratingFilter,  setRatingFilter] = useState("");
  const [statusFilter,  setStatusFilter] = useState("");
  const [fromDate,      setFromDate]     = useState(initialRange.fromDate);
  const [toDate,        setToDate]       = useState(initialRange.toDate);
  const [showFilters,   setShowFilters]  = useState(false);

  const [sortKey,     setSortKey]     = useState("createdAt");
  const [sortDir,     setSortDir]     = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize,    setPageSize]    = useState(10);

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage - 1, size: pageSize,
        ...(filterText   && { search: filterText }),
        ...(ratingFilter && { rating: Number(ratingFilter) }),
        ...(statusFilter !== "" && { resolved: statusFilter === "true" }),
        ...(fromDate && { fromDate }),
        ...(toDate   && { toDate }),
      };
      const result = await customerService.getFeedbackList(params);
      setFeedbacks(result?.content ?? []);
      setTotalElements(result?.totalElements ?? 0);
      setTotalPages(result?.totalPages ?? 1);
      setSummary(result?.summary ?? null);
    } catch (err) {
      console.error(err);
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterText, ratingFilter, statusFilter, fromDate, toDate]);

  useEffect(() => { fetchFeedbacks(); }, [fetchFeedbacks]);
  useEffect(() => { if (renderActions) renderActions({ onRefresh: fetchFeedbacks }); }, [renderActions, fetchFeedbacks]);
  useEffect(() => { setCurrentPage(1); }, [filterText, ratingFilter, statusFilter, fromDate, toDate]);

  const handleDateApply = useCallback((from, to) => {
    setFromDate(from); setToDate(to); setCurrentPage(1);
  }, []);

  const sorted = useMemo(() => {
    if (!sortKey) return feedbacks;
    return [...feedbacks].sort((a, b) => {
      let av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      const an = Number(av), bn = Number(bv);
      if (!isNaN(an) && !isNaN(bn)) return sortDir === "asc" ? an - bn : bn - an;
      av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [feedbacks, sortKey, sortDir]);

  const handleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const SortBtn = ({ label, k }) => (
    <button onClick={() => handleSort(k)}
      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
        sortKey === k ? "bg-rose-500 text-white border-rose-500 shadow-sm" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
      }`}>
      {label}
      {sortKey === k
        ? sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />
        : <ArrowUpDown size={10} className="text-gray-300" />}
    </button>
  );

  const activeCount = [ratingFilter, statusFilter].filter(Boolean).length;
  const clearAll    = () => { setRatingFilter(""); setStatusFilter(""); setFilterText(""); };

  return (
    <div className="min-h-full px-5 py-6" style={{ background:"linear-gradient(160deg,#fafafa 0%,#fff5f6 45%,#f0fdf4 100%)" }}>
      <div className="space-y-4">

        <DateRangeBar fromDate={fromDate} toDate={toDate} onApply={handleDateApply} onRefresh={fetchFeedbacks} />

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Total Reviews" value={summary.totalFeedbacks ?? 0}
              icon={MessageSquare} iconBg="bg-rose-50" iconColor="text-rose-500" delay={0} />
            <StatCard
              label="Avg. Rating"
              value={summary.averageRating
                ? <span className="flex items-center gap-1">{summary.averageRating.toFixed(1)}<Star size={12} className="s-on" /></span>
                : "—"}
              icon={Star} iconBg="bg-amber-50" iconColor="text-amber-500" valueCls="text-amber-600" delay={60} />
            <StatCard label="Unresolved" value={summary.unresolvedCount ?? 0}
              icon={AlertCircle} iconBg="bg-orange-50" iconColor="text-orange-500" valueCls="text-orange-600" delay={120} />
            <StatCard
              label="Resolved"
              value={(summary.totalFeedbacks ?? 0) - (summary.unresolvedCount ?? 0)}
              icon={CheckCircle} iconBg="bg-emerald-50" iconColor="text-emerald-500" valueCls="text-emerald-600" delay={180} />
          </div>
        )}

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex-1 min-w-[210px] max-w-sm relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input type="text" placeholder="Search by name or phone…" value={filterText}
              onChange={e => setFilterText(e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 transition-all"
            />
            {filterText && (
              <button onClick={() => setFilterText("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={13} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-400 font-medium mr-0.5">Sort:</span>
            <SortBtn label="Newest" k="createdAt" />
            <SortBtn label="Rating" k="overallRating" />
            <SortBtn label="Name"   k="customerName" />
          </div>

          <button onClick={() => setShowFilters(v => !v)}
            className={`relative flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium rounded-xl border shadow-sm transition-all ${
              showFilters || activeCount > 0 ? "bg-rose-500 border-rose-500 text-white" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
            }`}>
            <SlidersHorizontal size={14} />Filter
            {activeCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white border-2 border-rose-500 text-rose-600 text-[9px] font-bold flex items-center justify-center">
                {activeCount}
              </span>
            )}
          </button>

          {(activeCount > 0 || filterText) && (
            <button onClick={clearAll} className="flex items-center gap-1 px-2.5 py-2.5 text-xs text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl border border-transparent transition-all">
              <X size={12} />Clear
            </button>
          )}

          {!loading && (
            <p className="ml-auto text-xs text-gray-400 font-medium tabular-nums">
              {totalElements.toLocaleString()} review{totalElements !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        {/* ── Filter Panel ── */}
        {showFilters && (
          <div className="fb-panel bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
            <div className="grid grid-cols-2 gap-3 max-w-sm">
              {[
                { label:"Rating", node:
                  <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-400 outline-none cursor-pointer">
                    <option value="">All Ratings</option>
                    {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} ★</option>)}
                  </select>
                },
                { label:"Status", node:
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-400 outline-none cursor-pointer">
                    <option value="">All Status</option>
                    <option value="false">Pending</option>
                    <option value="true">Resolved</option>
                  </select>
                },
              ].map(({ label, node }) => (
                <div key={label} className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</label>
                  {node}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Cards Grid ── */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <ShimmerCard key={i} />)}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-5">
            <div className="w-20 h-20 rounded-3xl flex items-center justify-center" style={{ background:"linear-gradient(135deg,#fff1f2,#ffe4e6)" }}>
              <MessageSquare className="w-9 h-9 text-rose-300" />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-700">No feedback yet</p>
              <p className="text-sm text-gray-400 mt-1">
                {filterText || activeCount > 0 ? "Try adjusting your filters or search" : "Customer reviews will show up here once submitted"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {sorted.map((item, idx) => <FeedbackCard key={item.id} item={item} idx={idx} />)}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && totalElements > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl px-4 shadow-sm">
            <Pagination
              totalPages={totalPages} currentPage={currentPage}
              onPageChange={setCurrentPage} totalItems={totalElements}
              pageSize={pageSize} onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              pageSizeOptions={[10, 20, 50]}
            />
          </div>
        )}
      </div>
    </div>
  );
}